import React, { useState, useEffect } from "react";
import { LuPencil } from "react-icons/lu";
import { Formik } from "formik";
import * as Yup from "yup";
import { Row, Col } from "react-bootstrap";
import NewTableComponent from "../../components/NewTableComponent";
import Breadcrumbs from "../../components/Breadcrumbs";
import CustomModal from "../../components/CustomModal";
import Input from "../../components/input";
import Select from "../../components/Select";
import Button from "../../components/button";
import MultiLineText from "../../components/MultiLineText";
import { apiService } from "../../services/apiService";
import { successNotify, errorNotify } from "../../utils/toast";
import {
  formatLocalDateTime,
} from "../../utils/helper";
import { plusIcon } from "../../utils/svgs";

const Roles = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiRows, setApiRows] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1 });
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    id: "",
    name: "",
  });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [usersOptions, setUsersOptions] = useState([]);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchAssignDropdowns = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        apiService.getAllUsersDropdown(),
        apiService.getRolesDropdown(),
      ]);

      const rawUsers = usersRes?.data || [];
      const rawRoles = rolesRes?.data || [];

      setUsersOptions(
        rawUsers.map((u) => ({
          value: u.value,
          label: u.label,
        }))
      );

      setRolesOptions(
        rawRoles.map((r) => ({
          value: r.id,
          label: r.name,
        }))
      );
    } catch (error) {
      errorNotify(error || "Failed to load users or roles dropdowns");
    }
  };

  const handleOpenAssignRole = () => {
    setSelectedUser(null);
    setSelectedRole(null);
    setShowAssignModal(true);
    fetchAssignDropdowns();
  };

  const handleAssignRoleSubmit = async () => {
    if (!selectedUser || !selectedRole) {
      errorNotify("Please select both User and Role.");
      return;
    }
    setAssignLoading(true);
    try {
      const payload = {
        user_id: Number(selectedUser.value),
        role_id: Number(selectedRole.value),
      };
      await apiService.assignRole(payload);
      successNotify("Role assigned successfully");
      setShowAssignModal(false);
    } catch (error) {
      errorNotify(error || "Failed to assign role");
    } finally {
      setAssignLoading(false);
    }
  };

  const breadcrumbsLinks = [
    { name: "Access Management", url: "/roles" },
    { name: "Roles", url: "/roles" },
  ];

  const roleValidationSchema = Yup.object().shape({
    name: Yup.string().required("Role Name is required"),
  });

  // Fetch paginated roles from API
  useEffect(() => {
    let isMounted = true;

    const fetchRolesList = async () => {
      setLoading(true);
      try {
        const response = await apiService.getRoles(currentPage, limit, searchText);
        if (!isMounted) return;

        const payload = response?.data || {};
        const nextRows = Array.isArray(payload?.results || payload?.data)
          ? payload.results || payload.data
          : [];

        setApiRows(nextRows);
        setMeta({
          totalItems: Number(payload?.meta?.totalItems || payload?.total || nextRows.length),
          totalPages: Number(payload?.meta?.totalPages || 1),
        });
      } catch (error) {
        if (!isMounted) return;
        setApiRows([]);
        setMeta({ totalItems: 0, totalPages: 1 });
        errorNotify(error || "Failed to fetch roles");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRolesList();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, refetchTrigger]);

  const handleOpenAdd = () => {
    setInitialFormValues({
      id: "",
      name: "",
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = async (row) => {
    try {
      setLoading(true);
      const res = await apiService.getRoleDetail(row.id);
      const detail = res?.data || {};
      setInitialFormValues({
        id: detail.id || "",
        name: detail.name || "",
      });
      setShowFormModal(true);
    } catch (error) {
      errorNotify(error || "Failed to fetch role details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (values.id) {
        // Edit existing Role
        const payload = {
          role_id: Number(values.id),
          name: values.name,
        };
        await apiService.updateRole(payload);
        successNotify("Role updated successfully");
      } else {
        // Create new Role
        const payload = {
          name: values.name,
        };
        await apiService.createRole(payload);
        successNotify("Role created successfully");
      }

      setShowFormModal(false);
      setRefetchTrigger((prev) => prev + 1);
    } catch (error) {
      errorNotify(error || "Failed to save role");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      name: "Role ID",
      key: "id",
      selector: (row) => row?.id,
      tableColumn: true,
      filterName: "roleIdFilter",
      type: "number",
      minWidth: "120px",
    },
    {
      name: "Role Name",
      key: "name",
      selector: (row) => row?.name,
      cell: (row) => (
        <MultiLineText text={row?.name || "-"} maxWidth="250px" />
      ),
      tableColumn: true,
      filterName: "roleNameFilter",
      type: "text",
      minWidth: "250px",
    },
    {
      name: "Created At",
      key: "created_at",
      selector: (row) => formatLocalDateTime(row?.created_at),
      tableColumn: true,
      minWidth: "250px",
    },
    {
      name: "Action",
      key: "action",
      cell: (row) => (
        <div className="table_action_btn">
          <button
            type="button"
            className="edit_btn"
            title="Edit role"
            onClick={() => handleOpenEdit(row)}
          >
            <LuPencil />
          </button>
        </div>
      ),
      tableColumn: false,
      minWidth: "120px",
      grow: 0,
    },
  ];

  return (
    <div className="trip_cost_estimation_page finance_settings_page">
      <Breadcrumbs values={["Access Management", "Roles"]} />

      <div className="access_view">
        <h2>Roles</h2>
        <div className="services_buttons">
          <button type="button" onClick={handleOpenAssignRole}>
            {plusIcon} Assign Role
          </button>
          <button type="button" onClick={handleOpenAdd}>
            {plusIcon} Add Role
          </button>
        </div>
      </div>

      <div className="mt-3">
        <NewTableComponent
          showAddBtn={false}
          columns={columns}
          data={apiRows}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          limit={limit}
          setLimit={setLimit}
          totalItems={meta.totalItems}
          totalPages={meta.totalPages}
          onSearch={setSearchText}
          searchValue={searchText}
          loading={loading}
          paginationLoading={loading}
          setSearchFilters={setSearchFilters}
          searchFilters={searchFilters}
          limitOptions={[10, 20, 50]}
          showFilter={true}
          showPagination={true}
        />
      </div>

      {/* Form Modal */}
      <CustomModal
        show={showFormModal}
        handleClose={() => setShowFormModal(false)}
        heading={initialFormValues.id ? "Edit Role" : "Add Role"}
        size="lg"
      >
        <Formik
          initialValues={initialFormValues}
          validationSchema={roleValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            submitForm,
            isSubmitting,
          }) => (
            <div>
              <Row>
                <Col md={12}>
                  <Input
                    label="Role Name"
                    placeholder="Enter Role Name"
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.name}
                    touched={touched.name}
                    required
                  />
                </Col>

                <Col md={12}>
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      cancelBtn={() => setShowFormModal(false)}
                      submitBtn={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        submitForm();
                      }}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      submitText="Save"
                      type="button"
                      shouldRow={false}
                      buttonExtraCss="fuel_add_btn"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Formik>
      </CustomModal>

      {/* Assign Role Modal */}
      <CustomModal
        show={showAssignModal}
        handleClose={() => setShowAssignModal(false)}
        heading="Assign Role"
        size="md"
      >
        <div>
          <Row>
            <Col md={12} className="mb-3">
              <Select
                label="Select User"
                placeholder="Select User"
                options={usersOptions}
                value={selectedUser}
                onChange={(option) => setSelectedUser(option)}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </Col>

            <Col md={12} className="mb-3">
              <Select
                label="Select Role"
                placeholder="Select Role"
                options={rolesOptions}
                value={selectedRole}
                onChange={(option) => setSelectedRole(option)}
                menuPlacement="auto"
                menuPortalTarget={document.body}
              />
            </Col>

            <Col md={12}>
              <div className="d-flex gap-2 mt-3">
                <Button
                  cancelBtn={() => setShowAssignModal(false)}
                  submitBtn={handleAssignRoleSubmit}
                  loading={assignLoading}
                  disabled={assignLoading || !selectedUser || !selectedRole}
                  submitText="Save"
                  type="button"
                  shouldRow={false}
                  buttonExtraCss="fuel_add_btn"
                />
              </div>
            </Col>
          </Row>
        </div>
      </CustomModal>
    </div>
  );
};

export default Roles;
